/**
 * IndexedDB 工具函数，用于存储和读取大文件
 */

const DB_NAME = 'pdf-editor-db';
const STORE_NAME = 'large-files';
const DB_VERSION = 1;

// 打开数据库
const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB is not supported'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
  });
};

/**
 * 存储大文件到 IndexedDB
 * @param key 存储的键（使用 operateRecordId）
 * @param file 文件对象
 */
export const saveLargeFileToIndexedDB = async (key: string, file: File): Promise<void> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const fileData = {
      key: key,
      file: file,
      timestamp: Date.now(),
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.put(fileData);
      request.onsuccess = () => {
        console.log(`大文件已保存到 IndexedDB: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        resolve();
      };
      request.onerror = () => {
        reject(request.error);
      };
    });

    db.close();
  } catch (error) {
    console.error('保存大文件到 IndexedDB 失败:', error);
    throw error;
  }
};

/**
 * 从 IndexedDB 读取大文件
 * @param key 存储的键（使用 operateRecordId）
 * @returns 文件对象，如果不存在则返回 null
 */
export const getLargeFileFromIndexedDB = async (key: string): Promise<File | null> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    const fileData = await new Promise<any>((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => {
        reject(request.error);
      };
    });

    db.close();

    if (fileData && fileData.file) {
      console.log(`从 IndexedDB 恢复大文件: ${fileData.file.name} (${(fileData.file.size / 1024 / 1024).toFixed(2)}MB)`);
      return fileData.file;
    }

    return null;
  } catch (error) {
    console.error('从 IndexedDB 读取大文件失败:', error);
    return null;
  }
};

/**
 * 从 IndexedDB 删除大文件
 * @param key 存储的键（使用 operateRecordId）
 */
export const deleteLargeFileFromIndexedDB = async (key: string): Promise<void> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(key);
      request.onsuccess = () => {
        console.log(`已从 IndexedDB 删除大文件: ${key}`);
        resolve();
      };
      request.onerror = () => {
        reject(request.error);
      };
    });

    db.close();
  } catch (error) {
    console.error('从 IndexedDB 删除大文件失败:', error);
    throw error;
  }
};

/**
 * 清理过期的文件（可选，用于清理旧文件）
 * @param maxAge 最大保存时间（毫秒），默认 7 天
 */
export const cleanExpiredFilesFromIndexedDB = async (maxAge: number = 7 * 24 * 60 * 60 * 1000): Promise<void> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.openCursor();
    const now = Date.now();

    await new Promise<void>((resolve, reject) => {
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const fileData = cursor.value;
          if (now - fileData.timestamp > maxAge) {
            cursor.delete();
            console.log(`已清理过期文件: ${fileData.key}`);
          }
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => {
        reject(request.error);
      };
    });

    db.close();
  } catch (error) {
    console.error('清理过期文件失败:', error);
  }
};

